// Mobile Sortable extensions — touch/coarse-pointer drag and merge.
// Imported by lib/sortable.js.

const bindMobilePageDrag = function (deps) {
  const {
    Sortable,
    DRAG_CLASS,
    lineup,
    state,
    plugin,
    active,
    pagesInMain,
    centerInMain,
    scrollLineupNearEdge,
    ensureLineupSpacers,
    removeLineupSpacers,
    clamp,
    withDragArtifactsHidden,
    setStorySortableEnabled,
    isEditOn,
  } = deps

  const TARGETS_ID = 'wiki-page-drag-targets'
  const LABEL_ID = 'wiki-page-drag-label'
  const LINEUP_SHRUNK = 'wiki-page-lineup-shrunk'

  const REMOVE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">' +
    '<path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm3.5 13.1-1.4 1.4L12 13.4l-2.1 2.1-1.4-1.4L10.6 12 8.5 9.9l1.4-1.4L12 10.6l2.1-2.1 1.4 1.4L13.4 12l2.1 2.1z"/>' +
    '</svg>'

  const ensureTargets = function () {
    let el = document.getElementById(TARGETS_ID)
    if (el) return el
    el = document.createElement('div')
    el.id = TARGETS_ID
    el.className = 'page-drag-targets page-drag-targets-trash-only'
    el.setAttribute('aria-hidden', 'true')
    el.innerHTML =
      '<div class="page-drag-target page-drag-target-trash" data-action="trash" aria-label="Remove page from lineup">' +
      `<span class="page-drag-target-icon">${REMOVE_SVG}</span></div>`
    document.body.appendChild(el)
    return el
  }

  const ensureLabel = function () {
    let el = document.getElementById(LABEL_ID)
    if (el) return el
    el = document.createElement('div')
    el.id = LABEL_ID
    el.className = 'wiki-page-drag-label'
    el.setAttribute('aria-hidden', 'true')
    el.innerHTML =
      '<div class="wiki-page-drag-label-page">' +
      '<div class="wiki-page-drag-label-title"></div>' +
      '</div>'
    document.body.appendChild(el)
    return el
  }

  const pageTitle = pageEl => {
    const text = pageEl?.querySelector?.('h1')?.textContent || ''
    return text.replace(/\s+/g, ' ').trim() || 'Page'
  }

  return function initMobilePageSortable(mainEl) {
    if (!mainEl) return null
    if (mainEl._wikiPageSortable) {
      mainEl._wikiPageSortable.destroy()
      mainEl._wikiPageSortable = null
    }

    let originalPageIndex = null
    let $dragging = null
    let dragOriginY = null
    let activeTarget = null
    let lastPointerX = null
    let lastPointerY = null
    let edgeScrollRaf = null
    let lastScrollTs = 0
    let exitedOffTop = false
    const spacers = {}
    let capturedEl = null
    let capturedPointerId = null

    const helperEl = () => {
      const fallback = document.querySelector('.sortable-fallback.page')
      if (fallback) return fallback
      if (Sortable.ghost?.classList?.contains('page')) return Sortable.ghost
      return document.querySelector(`.${DRAG_CLASS}`)
    }

    const pageNodes = () => pagesInMain(mainEl)

    const stopEdgeScroll = function () {
      if (edgeScrollRaf != null) {
        cancelAnimationFrame(edgeScrollRaf)
        edgeScrollRaf = null
      }
      lastScrollTs = 0
    }

    const edgeScrollTick = function (ts) {
      edgeScrollRaf = null
      if (!lastScrollTs) lastScrollTs = ts
      const dt = Math.min(0.05, (ts - lastScrollTs) / 1000)
      lastScrollTs = ts
      const { dir, speed } = scrollLineupNearEdge(lastPointerX, mainEl)
      if (dir && speed > 0) mainEl.scrollLeft += dir * speed * dt
      edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
    }

    const startEdgeScroll = function () {
      if (edgeScrollRaf != null) return
      lastScrollTs = 0
      edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
    }

    const eventY = e => {
      if (e == null) return null
      const t = e.changedTouches?.[0] || e.touches?.[0]
      if (t && typeof t.clientY === 'number') return t.clientY
      if (typeof e.clientY === 'number') return e.clientY
      return null
    }

    const eventX = e => {
      if (e == null) return null
      const t = e.changedTouches?.[0] || e.touches?.[0]
      if (t && typeof t.clientX === 'number') return t.clientX
      if (typeof e.clientX === 'number') return e.clientX
      return null
    }

    const capturePointer = function (oe) {
      if (!oe || typeof oe.pointerId !== 'number') return
      const el = document.body
      try {
        el.setPointerCapture(oe.pointerId)
        capturedEl = el
        capturedPointerId = oe.pointerId
      } catch (_) {
        try {
          oe.target?.setPointerCapture(oe.pointerId)
          capturedEl = oe.target
          capturedPointerId = oe.pointerId
        } catch (_) {
          /* not a pointer event */
        }
      }
    }

    const trashStripBottom = function () {
      const node = document.getElementById(TARGETS_ID)?.querySelector('.page-drag-target-trash')
      return node ? node.getBoundingClientRect().bottom : 20
    }

    const showTrash = function () {
      const targets = ensureTargets()
      const canRemove = pageNodes().length > 1
      targets.querySelector('[data-action="trash"]')?.classList.toggle('is-unavailable', !canRemove)
      targets.classList.add('is-active')
      targets.setAttribute('aria-hidden', 'false')
    }

    const hideTrash = function () {
      const targets = document.getElementById(TARGETS_ID)
      if (!targets) return
      targets.classList.remove('is-active')
      targets.setAttribute('aria-hidden', 'true')
      targets.querySelectorAll('.page-drag-target').forEach(t => t.classList.remove('is-hot'))
    }

    const moveLabel = function (clientX, clientY) {
      const label = document.getElementById(LABEL_ID)
      if (!label?.classList.contains('is-active')) return
      if (typeof clientX !== 'number' || typeof clientY !== 'number') return
      label.style.transform = `translate(${clientX - 42}px, ${clientY - 140}px)`
    }

    const showLabel = function (title, clientX, clientY) {
      const label = ensureLabel()
      const titleEl = label.querySelector('.wiki-page-drag-label-title')
      if (titleEl) titleEl.textContent = title
      label.classList.add('is-active')
      label.classList.remove('is-pending-remove')
      label.setAttribute('aria-hidden', 'false')
      moveLabel(clientX, clientY)
    }

    const hideLabel = function () {
      const label = document.getElementById(LABEL_ID)
      if (!label) return
      label.classList.remove('is-active', 'is-pending-remove')
      label.setAttribute('aria-hidden', 'true')
      label.style.transform = ''
    }

    const setLineupShrunk = function (on) {
      document.body.classList.toggle(LINEUP_SHRUNK, !!on)
    }

    const setPendingRemove = function (on) {
      if (!$dragging?.length) return
      const fallback = helperEl()
      const label = document.getElementById(LABEL_ID)
      $dragging.toggleClass('pending-remove', !!on)
      label?.classList.toggle('is-pending-remove', !!on)
      if (!fallback) return
      fallback.classList.toggle('pending-remove', !!on)
      fallback.classList.remove('wiki-page-ghost')
      fallback.style.transition = 'opacity 300ms'
      fallback.style.setProperty('opacity', on ? '0.2' : '1', 'important')
    }

    const hitTrash = function (clientX, clientY) {
      if (typeof clientY !== 'number') return false
      const targets = document.getElementById(TARGETS_ID)
      if (!targets?.classList.contains('is-active')) return false
      const node = targets.querySelector('.page-drag-target-trash:not(.is-unavailable)')
      if (!node) return false
      if (clientY <= 0) return true
      if (typeof dragOriginY === 'number' && clientY >= dragOriginY) return false
      return clientY <= node.getBoundingClientRect().bottom
    }

    const updateTrashHover = function (clientX, clientY) {
      if (typeof clientY === 'number') {
        if (clientY <= 0) exitedOffTop = true
        else if (clientY > trashStripBottom()) exitedOffTop = false
      }
      const over = exitedOffTop || hitTrash(clientX, clientY)
      activeTarget = over ? 'trash' : null
      document
        .getElementById(TARGETS_ID)
        ?.querySelector('.page-drag-target-trash')
        ?.classList.toggle('is-hot', over)
      setPendingRemove(over)
    }

    const onPointerMove = function (e) {
      const y = eventY(e)
      const x = eventX(e)
      if (typeof x === 'number') lastPointerX = x
      if (typeof y === 'number') lastPointerY = y
      if (dragOriginY == null && typeof y === 'number') dragOriginY = y
      moveLabel(x, y)
      updateTrashHover(x, y)
    }

    const onPointerLost = function (e) {
      const y = eventY(e)
      const x = eventX(e)
      if (typeof x === 'number') lastPointerX = x
      if (typeof y === 'number') lastPointerY = y
      const yNow = typeof y === 'number' ? y : lastPointerY
      if (typeof yNow !== 'number') {
        if (exitedOffTop) updateTrashHover(lastPointerX, 0)
        return
      }
      if (yNow <= 0 || yNow <= trashStripBottom()) {
        if (typeof dragOriginY !== 'number' || yNow < dragOriginY || yNow <= 0) exitedOffTop = true
      }
      updateTrashHover(typeof x === 'number' ? x : lastPointerX, yNow <= 0 ? 0 : yNow)
    }

    const stopTracking = function () {
      document.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointermove', onPointerMove, true)
      document.removeEventListener('touchmove', onPointerMove)
      document.removeEventListener('pointerup', onPointerLost, true)
      window.removeEventListener('pointerup', onPointerLost, true)
      document.removeEventListener('pointercancel', onPointerLost, true)
      window.removeEventListener('pointercancel', onPointerLost, true)
      document.removeEventListener('lostpointercapture', onPointerLost, true)
      stopEdgeScroll()
      hideTrash()
      hideLabel()
      removeLineupSpacers(spacers)
      setLineupShrunk(false)
      activeTarget = null
      exitedOffTop = false
      $dragging = null
      dragOriginY = null
      if (capturedEl && capturedPointerId != null) {
        try {
          capturedEl.releasePointerCapture(capturedPointerId)
        } catch (_) {
          /* already released */
        }
      }
      capturedEl = null
      capturedPointerId = null
    }

    const finishDrag = function ($page, removing, opts = {}) {
      if (!$page.hasClass('page')) return
      const $pages = $('.page')
      let index = $pages.index($('.active'))
      let firstItemIndex = $('.item').index($page.find('.item')[0])
      if (removing) {
        if ($pages.length === 1) return
        lineup.removeKey($page.data('key'))
        $page.remove()
        active.set($('.page')[index], true)
      } else {
        $page.removeClass('pending-remove')
        const newIndex = pageNodes().indexOf($page[0])
        lineup.changePageIndex($page.data('key'), newIndex >= 0 ? newIndex : index)
        active.set($page, !!opts.noScroll)
        if (originalPageIndex != null && originalPageIndex < (newIndex >= 0 ? newIndex : index)) {
          index = originalPageIndex
          firstItemIndex = $('.item').index($($('.page')[index]).find('.item')[0])
        }
        if (opts.instantCenter) {
          requestAnimationFrame(() => {
            centerInMain($page[0], mainEl)
            requestAnimationFrame(() => centerInMain($page[0], mainEl))
          })
        }
      }
      plugin.renderFrom(firstItemIndex)
      state.setUrl()
      if (window.debug) state.debugStates()
    }

    const sortable = Sortable.create(mainEl, {
      animation: 0,
      handle: '.page-handle',
      draggable: '.page',
      ghostClass: 'wiki-page-ghost',
      dragClass: DRAG_CLASS,
      direction: 'horizontal',
      forceFallback: true,
      fallbackOnBody: true,
      fallbackTolerance: 5,
      sort: true,
      group: { name: 'wiki-pages', pull: false, put: false },
      scroll: false,
      bubbleScroll: false,
      forceAutoScrollFallback: false,
      onMove(evt) {
        if (!evt?.dragged?.classList?.contains('page')) return false
        if (evt.to && !evt.to.classList?.contains('main') && evt.to !== mainEl) return false
        return true
      },
      onStart(evt) {
        if (!$(evt.item).hasClass('page')) return
        active.set($(evt.item), true)
        originalPageIndex = pageNodes().indexOf(evt.item)
        $dragging = $(evt.item)
        activeTarget = null
        exitedOffTop = false
        const oe = evt.originalEvent
        const x = eventX(oe)
        const y = eventY(oe)
        dragOriginY = y
        if (typeof x === 'number') lastPointerX = x
        if (typeof y === 'number') lastPointerY = y
        try {
          setStorySortableEnabled(false)
        } catch (_) {
          /* optional */
        }
        if (sortable.option) {
          sortable.option('scroll', false)
          sortable.option('animation', 0)
          sortable.option('swapThreshold', 0.85)
        }
        setLineupShrunk(true)
        ensureLineupSpacers(spacers, mainEl)
        requestAnimationFrame(() => {
          const ghost = mainEl.querySelector('.wiki-page-ghost')
          if (ghost) centerInMain(ghost, mainEl)
          requestAnimationFrame(() => {
            const ghost2 = mainEl.querySelector('.wiki-page-ghost')
            if (ghost2) centerInMain(ghost2, mainEl)
          })
        })
        showTrash()
        showLabel(pageTitle(evt.item), x, y)
        startEdgeScroll()
        capturePointer(oe)
        document.addEventListener('pointermove', onPointerMove, true)
        window.addEventListener('pointermove', onPointerMove, true)
        document.addEventListener('touchmove', onPointerMove, { passive: true })
        document.addEventListener('pointerup', onPointerLost, true)
        window.addEventListener('pointerup', onPointerLost, true)
        document.addEventListener('pointercancel', onPointerLost, true)
        window.addEventListener('pointercancel', onPointerLost, true)
        document.addEventListener('lostpointercapture', onPointerLost, true)
      },
      onEnd(evt) {
        const $page = $(evt.item)
        const oe = evt.originalEvent
        const y = eventY(oe) ?? lastPointerY
        const x = eventX(oe) ?? lastPointerX
        if (typeof y === 'number' && y <= 0) exitedOffTop = true
        updateTrashHover(x, y)
        const removing = activeTarget === 'trash' || exitedOffTop
        stopTracking()
        try {
          setStorySortableEnabled(isEditOn())
        } catch (_) {
          /* optional */
        }
        if ($page.parent().hasClass('story') || !$page.parent().hasClass('main')) {
          const kids = pageNodes()
          const ref = kids[Math.min(originalPageIndex ?? kids.length, kids.length)]
          if (ref) mainEl.insertBefore($page[0], ref)
          else mainEl.appendChild($page[0])
        }
        if (!removing) finishDrag($page, false, { noScroll: true, instantCenter: true })
        else finishDrag($page, removing)
      },
    })

    mainEl._wikiPageSortable = sortable
    return sortable
  }
}

/**
 * Item drag handle + edit button overlay (mobile touch UI).
 * @param {{ isEditOn: () => boolean }} deps
 */
const itemToolbar = function (deps) {
  const { isEditOn } = deps

  const HANDLE_CLASS = 'item-drag-handle'
  const EDIT_CLASS = 'item-edit-button'
  const TOOLBAR_CLASS = 'item-toolbar'

  const HANDLE_HTML = `<div class="${HANDLE_CLASS}" title="Drag to reorder" aria-label="Drag to reorder">⋮⋮</div>`
  const EDIT_HTML = `<button type="button" class="${EDIT_CLASS}" title="Edit" aria-label="Edit">✎</button>`
  const TOOLBAR_HTML = `<div class="${TOOLBAR_CLASS}">${HANDLE_HTML}${EDIT_HTML}</div>`

  const HANDLE_HEIGHT_PX = 44

  const overflowClips = v =>
    v === 'auto' || v === 'scroll' || v === 'hidden' || v === 'clip' || v === 'overlay'

  const visibleClipFor = item => {
    const vv = window.visualViewport
    const clip = {
      top: vv ? vv.offsetTop : 0,
      bottom: vv ? vv.offsetTop + vv.height : window.innerHeight,
    }
    let el = item.parentElement
    while (el && el !== document.documentElement) {
      const st = getComputedStyle(el)
      if (overflowClips(st.overflowY) || overflowClips(st.overflowX)) {
        const r = el.getBoundingClientRect()
        clip.top = Math.max(clip.top, r.top)
        clip.bottom = Math.min(clip.bottom, r.bottom)
      }
      el = el.parentElement
    }
    return clip
  }

  const needsDragHandle = () => window.matchMedia('(hover: none), (pointer: coarse)').matches

  let selectedId = null
  let pinBound = false
  let pinRaf = null
  let pinObserver = null
  let pinItem = null

  const itemId = $item => $item?.attr?.('data-id') || $item?.data?.('id') || null

  const toolbarSelector = `.${TOOLBAR_CLASS}, .${HANDLE_CLASS}, .${EDIT_CLASS}`

  const setLocalTop = (el, viewportY, fallbackParent) => {
    const parent = el.offsetParent || fallbackParent
    const pr = parent.getBoundingClientRect()
    const scaleY = pr.height ? parent.offsetHeight / pr.height : 1
    el.style.top = `${(viewportY - pr.top) * scaleY + (parent.scrollTop || 0)}px`
  }

  const pinSelectedToolbar = function () {
    const item = document.querySelector('.item.handle-selected')
    const toolbar = item?.querySelector(`.${TOOLBAR_CLASS}`)
    const handle = item?.querySelector(`.${HANDLE_CLASS}`)
    const edit = item?.querySelector(`.${EDIT_CLASS}`)
    if (!item || !toolbar || !handle) return

    const itemRect = item.getBoundingClientRect()
    const clip = visibleClipFor(item)
    const visibleBottom = Math.min(clip.bottom, itemRect.bottom)
    let y = Math.max(clip.top, itemRect.top)
    if (y + HANDLE_HEIGHT_PX > visibleBottom) {
      y = Math.max(itemRect.top, visibleBottom - HANDLE_HEIGHT_PX)
    }

    setLocalTop(toolbar, y, item)
    if (handle.offsetParent !== toolbar) {
      setLocalTop(handle, y, item)
      if (edit) setLocalTop(edit, y + 48, item)
    }
  }

  const schedulePin = function () {
    if (pinRaf != null) return
    if (
      document.body.classList.contains('wiki-item-handle-drag') ||
      document.body.classList.contains('wiki-item-lineup-dragging')
    ) {
      return
    }
    pinRaf = requestAnimationFrame(() => {
      pinRaf = null
      pinSelectedToolbar()
    })
  }

  const bindPin = function () {
    if (pinBound) return
    pinBound = true
    document.addEventListener('scroll', schedulePin, true)
    window.addEventListener('scroll', schedulePin)
    window.addEventListener('resize', schedulePin)
    window.visualViewport?.addEventListener('resize', schedulePin)
    window.visualViewport?.addEventListener('scroll', schedulePin)
    if (typeof ResizeObserver === 'function') {
      pinObserver = new ResizeObserver(schedulePin)
      pinObserver.observe(document.documentElement)
    }
  }

  const clearItemToolbar = $item => {
    if (pinObserver && pinItem && (!$item?.length || $item[0] === pinItem)) {
      pinObserver.unobserve(pinItem)
      pinItem = null
    }
    if ($item?.length) {
      $item.children(toolbarSelector).remove()
      $item.removeClass('handle-selected')
    } else {
      $(toolbarSelector).remove()
      $('.item.handle-selected').removeClass('handle-selected')
    }
  }

  const clear = function () {
    selectedId = null
    clearItemToolbar()
  }

  const mountItemToolbar = function ($item) {
    clearItemToolbar($item)
    $item.addClass('handle-selected')
    $item.prepend(TOOLBAR_HTML)
    bindPin()
    const item = $item[0]
    if (pinObserver && item) {
      if (pinItem && pinItem !== item) pinObserver.unobserve(pinItem)
      pinItem = item
      pinObserver.observe(item)
    }
    pinSelectedToolbar()
    requestAnimationFrame(pinSelectedToolbar)
    requestAnimationFrame(() => requestAnimationFrame(pinSelectedToolbar))
  }

  const select = function ($item) {
    if (!$item?.length) return null
    if (!isEditOn() || !needsDragHandle() || $item.hasClass('textEditing')) {
      clear()
      return null
    }
    const id = itemId($item)
    if (!id) return null
    selectedId = id
    $(toolbarSelector).remove()
    $('.item.handle-selected').removeClass('handle-selected')
    mountItemToolbar($item)
    return $item
  }

  const ensure = function ($item) {
    if (!$item || !$item.length) return $item
    clearItemToolbar($item)
    const id = itemId($item)
    const isSelected = selectedId && id && String(selectedId) === String(id)
    if (isSelected && isEditOn() && needsDragHandle() && !$item.hasClass('textEditing')) {
      mountItemToolbar($item)
    }
    return $item
  }

  const clearContent = function ($item) {
    $item.children().not(toolbarSelector).remove()
    return $item
  }

  const getSelectedId = () => selectedId

  return {
    HANDLE_CLASS,
    EDIT_CLASS,
    TOOLBAR_CLASS,
    needsDragHandle,
    ensure,
    clear,
    select,
    getSelectedId,
    clearContent,
    toolbarSelector,
  }
}

/**
 * Long-press selected journal → lineup shrink → merge drop (R-009).
 * @param {object} deps — shared helpers from sortable.js (pages, merge, edit gates, …)
 */
const bindJournalMergeTouch = function (deps) {
  const {
    dragHelpers,
    vertScrollCandidates,
    VERT_EDGE_PX,
    VERT_SCROLL_PX_PER_SEC,
    LONG_PRESS_MS,
    JOURNAL_BODY_SLOP_PX,
    MERGING,
    LINEUP_SHRUNK,
    SELECTED_CLASS,
    journalUnderPoint,
    clearMergeHover,
    handleMerging,
    clearTextSelection,
    isEditOn,
    needsDragHandle,
    setStorySortableEnabled,
    selectJournal,
    clearJournalSelection,
    suppressClickUntil,
    setSuppressClickUntil,
  } = deps

  return function bindJournalMerge() {
    let pending = null
    let session = null

    const mainEl = () => dragHelpers.getMain()

    const cancelPending = function () {
      if (!pending) return
      if (pending.raf != null) cancelAnimationFrame(pending.raf)
      if (pending.pointerId != null) {
        try {
          pending.captureEl?.releasePointerCapture?.(pending.pointerId)
        } catch (_) {
          /* optional */
        }
      }
      pending = null
    }

    const instantCenterEl = el => dragHelpers.centerInMain(el, mainEl())
    const ensureSpacers = () => dragHelpers.ensureLineupSpacers(session, mainEl())
    const removeSpacers = () => dragHelpers.removeLineupSpacers(session)
    const edgeScrollSpeed = clientX => dragHelpers.scrollLineupNearEdge(clientX, mainEl())

    const stopEdgeScroll = function () {
      if (session?.edgeRaf != null) {
        cancelAnimationFrame(session.edgeRaf)
        session.edgeRaf = null
      }
    }

    const updateHover = function () {
      if (!session) return
      clearMergeHover()
      const over = journalUnderPoint(session.pointerX, session.pointerY, session.journalEl)
      if (over) {
        over.classList.add('wiki-merge-hover')
        session.destEl = over
      } else {
        session.destEl = null
      }
    }

    const moveClone = function (x, y) {
      if (!session?.clone) return
      const scale = document.body.classList.contains(LINEUP_SHRUNK) ? 0.5 : 1
      const ox = session.offsetX ?? 24
      const oy = session.offsetY ?? 16
      session.clone.style.transform = `translate(${x - ox * scale}px, ${y - oy * scale}px) scale(${scale})`
    }

    const edgeTick = function (ts) {
      if (!session) return
      session.edgeRaf = null
      if (!session.lastScrollTs) session.lastScrollTs = ts
      const dt = Math.min(0.05, (ts - session.lastScrollTs) / 1000)
      session.lastScrollTs = ts

      const { dir, speed } = edgeScrollSpeed(session.pointerX)
      const main = mainEl()
      if (main && dir && speed > 0) {
        main.scrollLeft += dir * speed * dt
      }

      const candidates = vertScrollCandidates(
        session.pointerX,
        session.journalEl?.closest?.('.page'),
        session.destEl?.closest?.('.page')?.querySelector?.('.story'),
      )
      const destPage = session.destEl?.closest?.('.page')
      if (destPage && !candidates.includes(destPage)) candidates.push(destPage)
      dragHelpers.scrollPageNearEdge(candidates, session.pointerY, dt, VERT_EDGE_PX, VERT_SCROLL_PX_PER_SEC)

      updateHover()
      moveClone(session.pointerX, session.pointerY)
      session.edgeRaf = requestAnimationFrame(edgeTick)
    }

    const teardown = function () {
      if (!session) return
      stopEdgeScroll()
      session.clone?.remove()
      session.journalEl?.classList.remove('is-dragging')
      document.body.classList.remove(MERGING, LINEUP_SHRUNK)
      clearMergeHover()
      removeSpacers()
      document.removeEventListener('pointermove', onDragMove, { passive: false })
      document.removeEventListener('touchmove', onDragMove, { passive: false })
      document.removeEventListener('pointerup', onDragCommit)
      document.removeEventListener('pointercancel', onDragAbort)
      document.removeEventListener('touchend', onDragCommit)
      document.removeEventListener('mouseup', onDragCommit)
      document.removeEventListener('lostpointercapture', onDragAbort)
      window.removeEventListener('pagehide', onDragAbort)
      if (session.pointerId != null) {
        try {
          session.captureEl?.releasePointerCapture?.(session.pointerId)
        } catch (_) {
          /* optional */
        }
      }
      try {
        setStorySortableEnabled(isEditOn())
      } catch (_) {
        /* optional */
      }
      try {
        dragHelpers.setPageSortableEnabled(true)
      } catch (_) {
        /* optional */
      }
      session = null
    }

    const onDragMove = function (e) {
      if (!session) return
      if (session.pointerId != null && e.pointerId != null && e.pointerId !== session.pointerId) return
      const x = e.clientX ?? e.touches?.[0]?.clientX
      const y = e.clientY ?? e.touches?.[0]?.clientY
      if (typeof x !== 'number' || typeof y !== 'number') return
      session.pointerX = x
      session.pointerY = y
      moveClone(x, y)
      updateHover()
      e.preventDefault()
    }

    const onDragCommit = function (e) {
      if (!session || session.committed) return
      if (session.pointerId != null && e && e.pointerId != null && e.pointerId !== session.pointerId) {
        return
      }
      if (session.suppressCommitUntil && performance.now() < session.suppressCommitUntil) {
        return
      }
      session.committed = true
      const sourceEl = session.journalEl
      // R-009: resolve dest under pointer when session.destEl lost (pointercapture race).
      const destEl = session.destEl || journalUnderPoint(session.pointerX, session.pointerY, sourceEl)
      teardown()
      setSuppressClickUntil(performance.now() + 400)
      if (destEl) handleMerging(sourceEl, destEl)
      clearJournalSelection()
    }

    const onDragAbort = function (e) {
      if (!session) return
      if (session.pointerId != null && e && e.pointerId !== session.pointerId) return
      if (session.ignorePointerCancel && e?.type === 'lostpointercapture') {
        return
      }
      if (session.ignorePointerCancel && e?.type === 'pointercancel') {
        session.suppressCommitUntil = performance.now() + 400
        return
      }
      const journalEl = session.journalEl
      teardown()
      if (journalEl) selectJournal($(journalEl))
    }

    const beginDrag = function (journalEl, clientX, clientY, pointerId) {
      cancelPending()
      const rect = journalEl.getBoundingClientRect()
      const captureEl = document.body
      const clone = journalEl.cloneNode(true)
      clone.classList.add('journal-merge-clone')
      clone.classList.remove(SELECTED_CLASS, 'is-dragging')
      clone.style.position = 'fixed'
      clone.style.left = '0'
      clone.style.top = '0'
      clone.style.width = `${rect.width}px`
      clone.style.pointerEvents = 'none'
      clone.style.zIndex = '100050'
      clone.style.opacity = '0.9'
      clone.style.transformOrigin = 'top left'
      document.body.appendChild(clone)

      session = {
        journalEl,
        clone,
        pointerX: clientX,
        pointerY: clientY,
        pointerId,
        offsetX: clientX - rect.left,
        offsetY: clientY - rect.top,
        destEl: null,
        leftSpacer: null,
        rightSpacer: null,
        edgeRaf: null,
        lastScrollTs: 0,
        ignorePointerCancel: true,
        suppressCommitUntil: performance.now() + 500,
        captureEl,
      }

      clearTextSelection()
      if (typeof pointerId === 'number') {
        try {
          captureEl.setPointerCapture?.(pointerId)
        } catch (_) {
          /* optional */
        }
      }

      journalEl.classList.add('is-dragging')
      try {
        setStorySortableEnabled(false)
      } catch (_) {
        /* optional */
      }
      try {
        dragHelpers.setPageSortableEnabled(false)
      } catch (_) {
        /* optional */
      }

      document.addEventListener('pointermove', onDragMove, { passive: false })
      document.addEventListener('touchmove', onDragMove, { passive: false })
      document.addEventListener('pointerup', onDragCommit)
      document.addEventListener('pointercancel', onDragAbort)
      document.addEventListener('touchend', onDragCommit)
      document.addEventListener('mouseup', onDragCommit)
      document.addEventListener('lostpointercapture', onDragAbort)
      window.addEventListener('pagehide', onDragAbort)

      moveClone(clientX, clientY)

      requestAnimationFrame(() => {
        if (!session) return
        document.body.classList.add(MERGING)
        requestAnimationFrame(() => {
          if (!session) return
          document.body.classList.add(LINEUP_SHRUNK)
          ensureSpacers()
          const srcPage = journalEl.closest('.page')
          if (srcPage) instantCenterEl(srcPage)
          moveClone(session.pointerX, session.pointerY)
          updateHover()
          session.edgeRaf = requestAnimationFrame(edgeTick)
          session.suppressCommitUntil = performance.now() + 200
        })
      })
    }

    const pendingTick = function () {
      if (!pending) return
      pending.raf = null
      if (performance.now() - pending.t0 >= LONG_PRESS_MS) {
        const { journalEl, x, y, pointerId } = pending
        cancelPending()
        beginDrag(journalEl, x, y, pointerId)
        return
      }
      pending.raf = requestAnimationFrame(pendingTick)
    }

    const armPending = function (journalEl, e) {
      cancelPending()
      pending = {
        journalEl,
        x: e.clientX,
        y: e.clientY,
        startX: e.clientX,
        startY: e.clientY,
        pointerId: e.pointerId,
        t0: performance.now(),
        raf: null,
        captureEl: document.body,
      }
      if (typeof e.pointerId === 'number') {
        try {
          document.body.setPointerCapture?.(e.pointerId)
        } catch (_) {
          /* optional */
        }
      }
      pending.raf = requestAnimationFrame(pendingTick)
    }

    document.addEventListener(
      'pointerdown',
      function (e) {
        if (pending) return
        if (session) {
          const stuckJournal = session.journalEl
          teardown()
          if (stuckJournal) selectJournal($(stuckJournal))
          return
        }
        if (!isEditOn() || !needsDragHandle()) return
        if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return
        if (e.button != null && e.button !== 0) return
        const target = e.target
        if (!(target instanceof Element)) return
        if (target.closest('.action, .control-buttons, a, button')) return
        const journalEl = target.closest(`.page .journal.${SELECTED_CLASS}`)
        if (!journalEl) return
        e.preventDefault()
        e.stopPropagation()
        armPending(journalEl, e)
      },
      true,
    )

    document.addEventListener(
      'pointermove',
      function (e) {
        if (!pending || e.pointerId !== pending.pointerId) return
        const dx = e.clientX - pending.startX
        const dy = e.clientY - pending.startY
        pending.x = e.clientX
        pending.y = e.clientY
        e.preventDefault()
        if (dx * dx + dy * dy > JOURNAL_BODY_SLOP_PX * JOURNAL_BODY_SLOP_PX) {
          cancelPending()
          setSuppressClickUntil(performance.now() + 350)
        }
      },
      { capture: true, passive: false },
    )

    document.addEventListener(
      'pointerup',
      function (e) {
        if (pending && (!e || e.pointerId === pending.pointerId)) cancelPending()
      },
      true,
    )

    document.addEventListener(
      'contextmenu',
      function (e) {
        if (!needsDragHandle()) return
        if (e.target?.closest?.(`.page .journal.${SELECTED_CLASS}`)) e.preventDefault()
      },
      true,
    )
  }
}

/**
 * Mobile cross-page / lineup item drag (long-press selected item body).
 * @param {object} deps — shared helpers from sortable.js
 */
const makeLineupItemDrag = function (deps) {
  // R-029: these live in sortable.js — must be passed (free refs throw at beginDrag).
  for (const k of [
    'syncDragItemTypography',
    'resolveLineupInsertY',
    'cappedPlaceholderHeightPx',
    'placeItemOnPage',
    'itemDropAllowed',
  ]) {
    if (typeof deps?.[k] !== 'function') {
      throw new Error()
    }
  }
  const {
    dragHelpers,
    MOVE_SVG,
    COPY_SVG,
    needsDragHandle,
    isEditOn,
    clear,
    select,
    HANDLE_CLASS,
    EDIT_CLASS,
    CONTROLS_CLASS,
    setStorySortableEnabled,
    vertScrollCandidates,
    LINEUP_SHRUNK,
    syncDragItemTypography,
    resolveLineupInsertY,
    cappedPlaceholderHeightPx,
    placeItemOnPage,
    itemDropAllowed,
  } = deps

  // Cross-page item drag (long-press selected item body).

  const DRAGGING = 'wiki-item-lineup-dragging'
  const HELPER_ID = 'wiki-item-lineup-helper'
  const OVERLAY_ID = 'wiki-item-lineup-overlay'
  const PLACEHOLDER_CLASS = 'wiki-item-lineup-placeholder'
  const ORIGIN_CLASS = 'wiki-item-lineup-origin'
  const ORIGIN_HIDDEN = 'wiki-item-lineup-origin-hidden'
  const ORIGIN_COPY = 'wiki-item-lineup-origin-copy'

  // Long-press on the selected item body (not the handle).
  const LONG_PRESS_MS = 380
  const BODY_SLOP_PX = 36
  const VERT_EDGE_PX = 120
  const VERT_SCROLL_PX_PER_SEC = 1100
  // Frame sits outside the story column so inset borders do not cover item text.
  const OVERLAY_OUTSET_PX = 4

  const ensureHelper = function () {
    let el = document.getElementById(HELPER_ID)
    if (el) return el
    el = document.createElement('div')
    el.id = HELPER_ID
    el.className = 'wiki-item-lineup-helper wiki-item-drag item'
    el.setAttribute('aria-hidden', 'true')
    document.body.appendChild(el)
    return el
  }

  const ensureOverlay = function () {
    let el = document.getElementById(OVERLAY_ID)
    if (el) return el
    el = document.createElement('div')
    el.id = OVERLAY_ID
    el.className = 'wiki-item-lineup-overlay'
    el.setAttribute('aria-hidden', 'true')
    el.innerHTML =
      `<div class="wiki-item-lineup-half wiki-item-lineup-half-move" data-action="move">` +
      `<span class="wiki-item-lineup-half-icon">${MOVE_SVG}</span>` +
      `<span class="wiki-item-lineup-half-label">Move</span></div>` +
      `<div class="wiki-item-lineup-half wiki-item-lineup-half-copy" data-action="copy">` +
      `<span class="wiki-item-lineup-half-icon">${COPY_SVG}</span>` +
      `<span class="wiki-item-lineup-half-label">Copy</span></div>`
    document.body.appendChild(el)
    return el
  }

  const ensurePlaceholder = function () {
    let el = document.querySelector(`.${PLACEHOLDER_CLASS}`)
    if (el) return el
    el = document.createElement('div')
    el.className = `${PLACEHOLDER_CLASS} item-placeholder`
    el.setAttribute('aria-hidden', 'true')
    return el
  }

  let suppressItemClickUntil = 0

  const shouldSuppressItemClick = () => performance.now() < suppressItemClickUntil

  const bindLineupItemDrag = function () {
    let pending = null
    let session = null

    const mainEl = () => dragHelpers.getMain()

    const cancelPending = function () {
      if (!pending) return
      if (pending.raf != null) cancelAnimationFrame(pending.raf)
      pending.$item?.[0]?.classList?.remove('wiki-longpress-pending')
      pending = null
    }

    const pageNodes = () => dragHelpers.pagesInMain(mainEl())
    const instantCenterEl = el => dragHelpers.centerInMain(el, mainEl())
    const nearestPageIndex = () => dragHelpers.nearestPageIndexIn(mainEl())
    const ensureSpacers = () => dragHelpers.ensureLineupSpacers(session, mainEl())
    const removeSpacers = () => dragHelpers.removeLineupSpacers(session)
    const edgeScrollSpeed = clientX => dragHelpers.scrollLineupNearEdge(clientX, mainEl())

    const stopEdgeScroll = function () {
      if (session?.edgeRaf != null) {
        cancelAnimationFrame(session.edgeRaf)
        session.edgeRaf = null
      }
    }

    const edgeTick = function (ts) {
      if (!session) return
      session.edgeRaf = null
      if (!session.lastScrollTs) session.lastScrollTs = ts
      const dt = Math.min(0.05, (ts - session.lastScrollTs) / 1000)
      session.lastScrollTs = ts

      const { dir, speed } = edgeScrollSpeed(session.pointerX)
      const main = mainEl()
      if (main && dir && speed > 0) {
        main.scrollLeft += dir * speed * dt
      }

      const candidates = vertScrollCandidates(
        session.pointerX,
        session.$sourcePage?.[0] || session.$item?.[0]?.closest?.('.page'),
        session.$destPage?.[0]?.querySelector?.('.story'),
      )
      if (session.$destPage?.[0] && !candidates.includes(session.$destPage[0])) {
        candidates.push(session.$destPage[0])
      }
      dragHelpers.scrollPageNearEdge(candidates, session.pointerY, dt, VERT_EDGE_PX, VERT_SCROLL_PX_PER_SEC)

      updateHoverUi()
      session.edgeRaf = requestAnimationFrame(edgeTick)
    }

    const moveHelper = function (x, y) {
      const helper = document.getElementById(HELPER_ID)
      if (!helper?.classList.contains('is-active') || !session) return
      if (typeof x !== 'number' || typeof y !== 'number') return
      const ox = session.grabOffsetX ?? 24
      const oy = session.grabOffsetY ?? 16
      // Full-size like desktop; match page lineup shrink (0.5) when crossing dragHelpers.
      const scale = document.body.classList.contains(LINEUP_SHRUNK) ? 0.5 : 1
      helper.style.transform = `translate(${x - ox * scale}px, ${y - oy * scale}px) scale(${scale})`
    }

    const syncOriginVisibility = function (copying) {
      if (!session?.$item?.length) return
      session.$item.toggleClass(ORIGIN_HIDDEN, !copying)
      session.$item.toggleClass(ORIGIN_COPY, !!copying)
    }

    const dropClipBottom = function () {
      const footer = document.querySelector('footer')
      const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight
      return Math.max(0, footerTop - 6)
    }

    const findInsertBefore = function (pageEl, clientY) {
      const story = pageEl.querySelector('.story')
      if (!story) return { $before: $(), markerParent: null, markerRef: null }
      const items = [
        ...story.querySelectorAll(`:scope > .item:not(.${ORIGIN_CLASS}):not(.${PLACEHOLDER_CLASS})`),
      ]
      if (!items.length) return { $before: $(), markerParent: story, markerRef: story.firstChild }

      const { y: resolvedY, forceEnd } = resolveLineupInsertY(pageEl, clientY)

      const clipBottom = dropClipBottom()
      const storyRect = story.getBoundingClientRect()
      let y = typeof resolvedY === 'number' ? resolvedY : storyRect.top
      y = Math.min(y, Math.min(storyRect.bottom - 2, clipBottom))

      if (forceEnd && items.length) {
        const last = items[items.length - 1]
        return { $before: $(last), markerParent: story, markerRef: last.nextSibling }
      }

      const rects = items.map(el => el.getBoundingClientRect())
      const firstRect = rects[0]
      // No max(28,…) floor — when the tall lead is dragged, the next visible item is short
      // and a 28px inset swallows the gap between the second and third lines (How To Wiki).
      const topZone = firstRect.top + Math.min(48, firstRect.height * 0.4)
      if (y < topZone) {
        return { $before: $(), markerParent: story, markerRef: items[0] }
      }

      const gapStickPad = h => Math.max(20, h * 0.5)

      for (let i = 1; i < rects.length; i++) {
        const prev = rects[i - 1]
        const next = rects[i]
        const prevBottom = prev.top + prev.height
        const gap = next.top - prevBottom
        if (gap > 0 && y > prevBottom && y < next.top) {
          return { $before: $(items[i - 1]), markerParent: story, markerRef: items[i] }
        }
        if (gap <= 0) {
          const gapMin = prevBottom - gapStickPad(prev.height)
          const gapMax = next.top + gapStickPad(next.height)
          if (y >= gapMin && y <= gapMax) {
            return { $before: $(items[i - 1]), markerParent: story, markerRef: items[i] }
          }
        }
      }

      let idx = 0
      for (let i = 0; i < rects.length; i++) {
        const mid = rects[i].top + rects[i].height / 2
        if (y >= mid) idx = i + 1
        else break
      }
      const markerRef = idx < items.length ? items[idx] : null
      const $before = idx === 0 ? $() : $(items[idx - 1])
      return { $before, markerParent: story, markerRef }
    }

    const updateHoverUi = function () {
      if (!session) return
      const nodes = pageNodes()
      const main = mainEl()
      const px = session.pointerX
      const py = session.pointerY
      const i =
        typeof px === 'number' && typeof py === 'number' && main
          ? dragHelpers.nearestPageIndexAt(main, px, py)
          : nearestPageIndex()
      const pageEl = i >= 0 ? nodes[i] : null
      session.$destPage = pageEl ? $(pageEl) : $()
      session.action = 'move'
      session.$before = $()

      const overlay = document.getElementById(OVERLAY_ID)
      const placeholder = ensurePlaceholder()
      const clearOverlayDropState = () => {
        overlay?.classList.remove('is-no-drop')
        overlay
          ?.querySelectorAll('.wiki-item-lineup-half')
          .forEach(h => h.classList.remove('is-hot', 'is-no-drop'))
      }
      if (!pageEl) {
        overlay?.classList.remove('is-active', 'labels-above', 'labels-below', 'move-only', 'is-no-drop')
        clearOverlayDropState()
        placeholder.remove()
        syncOriginVisibility(false)
        return
      }

      const sourceEl = session.$item?.closest?.('.page')?.[0]
      const sameSourcePage = !!(sourceEl && pageEl === sourceEl)
      const crossPageGhost = pageEl.classList.contains('ghost') && !sameSourcePage
      const $sourcePage = session.$sourcePage
      const $destPage = $(pageEl)

      const band = dragHelpers.pageDropBounds?.(pageEl)
      const pr = band
        ? {
            left: band.bandLeft,
            right: band.bandRight,
            top: band.bandTop,
            bottom: band.bandBottom,
            width: band.bandRight - band.bandLeft,
          }
        : (pageEl.querySelector('.story') || pageEl).getBoundingClientRect()
      const clipBottom = dropClipBottom()
      const top = Math.max(0, Math.round(pr.top))
      const bottom = Math.min(Math.round(pr.bottom), clipBottom)
      const height = Math.max(0, bottom - top)
      if (height < 24) {
        overlay?.classList.remove('is-active', 'move-only', 'is-no-drop')
        clearOverlayDropState()
        placeholder.remove()
        return
      }

      const midX = pr.left + pr.width / 2
      const wantCopy =
        !sameSourcePage && typeof session.pointerX === 'number' && session.pointerX >= midX
      session.action = wantCopy ? 'copy' : 'move'
      syncOriginVisibility(wantCopy)

      const dropOk = !crossPageGhost && itemDropAllowed($sourcePage, $destPage, { copy: wantCopy })

      const fingerY = typeof session.pointerY === 'number' ? session.pointerY : top + height / 2
      const labelsBelow = fingerY < top + height * 0.45

      if (overlay) {
        // Full page column (title/header through journal) — illegal drop frames whole target page.
        const outset = OVERLAY_OUTSET_PX
        overlay.style.top = `${top - outset}px`
        overlay.style.left = `${Math.round(pr.left) - outset}px`
        overlay.style.width = `${Math.round(pr.width) + outset * 2}px`
        overlay.style.height = `${height + outset * 2}px`
        overlay.classList.add('is-active')
        overlay.classList.toggle('move-only', sameSourcePage)
        overlay.classList.toggle('labels-below', labelsBelow)
        overlay.classList.toggle('labels-above', !labelsBelow)
        overlay.classList.toggle('is-no-drop', !dropOk)
        overlay.setAttribute('aria-hidden', 'false')
        const moveHalf = overlay.querySelector('.wiki-item-lineup-half-move')
        const copyHalf = overlay.querySelector('.wiki-item-lineup-half-copy')
        // Red frame only when drop is illegal — no floating Move/Copy labels.
        if (dropOk) {
          moveHalf?.classList.toggle('is-hot', !wantCopy)
          copyHalf?.classList.toggle('is-hot', wantCopy)
          moveHalf?.classList.remove('is-no-drop')
          copyHalf?.classList.remove('is-no-drop')
        } else {
          moveHalf?.classList.remove('is-hot', 'is-no-drop')
          copyHalf?.classList.remove('is-hot', 'is-no-drop')
        }
      }

      const { $before, markerParent, markerRef } = findInsertBefore(pageEl, session.pointerY)
      session.$before = $before
      placeholder.classList.toggle('is-no-drop', !dropOk)
      if (crossPageGhost) {
        placeholder.remove()
      } else {
        const phHeight = cappedPlaceholderHeightPx(session.itemHeight, pageEl)
        placeholder.style.height = `${phHeight}px`
        placeholder.style.minHeight = `${phHeight}px`
        placeholder.style.maxHeight = `${phHeight}px`
        if (markerParent) {
          if (markerRef) markerParent.insertBefore(placeholder, markerRef)
          else markerParent.appendChild(placeholder)
        }
      }
    }

    const teardown = function () {
      stopEdgeScroll()
      document.removeEventListener('pointermove', onDragMove)
      document.removeEventListener('touchmove', onDragMove)
      document.removeEventListener('pointerup', onDragCommit)
      document.removeEventListener('pointercancel', onDragAbort)
      document.removeEventListener('touchend', onDragCommit)
      document.removeEventListener('mouseup', onDragCommit)
      document.removeEventListener('lostpointercapture', onDragAbort)
      window.removeEventListener('pagehide', onDragAbort)
      if (session?.captureEl && session.pointerId != null) {
        try {
          session.captureEl.releasePointerCapture?.(session.pointerId)
        } catch (_) {
          /* already released */
        }
      }
      document.getElementById(HELPER_ID)?.classList.remove('is-active')
      const helper = document.getElementById(HELPER_ID)
      if (helper) {
        helper.innerHTML = ''
        helper.removeAttribute('style')
        helper.className = 'wiki-item-lineup-helper wiki-item-drag item'
      }
      const overlay = document.getElementById(OVERLAY_ID)
      if (overlay) {
        overlay.classList.remove('is-active', 'labels-above', 'labels-below', 'move-only', 'is-no-drop')
        overlay
          .querySelectorAll('.wiki-item-lineup-half')
          .forEach(h => h.classList.remove('is-hot', 'is-no-drop'))
        overlay.setAttribute('aria-hidden', 'true')
      }
      document.querySelector(`.${PLACEHOLDER_CLASS}`)?.remove()
      removeSpacers()
      document.body.classList.remove(LINEUP_SHRUNK, DRAGGING)
      session?.$item?.removeClass(`${ORIGIN_CLASS} ${ORIGIN_HIDDEN} ${ORIGIN_COPY}`)
      try {
        dragHelpers.setPageSortableEnabled(true)
      } catch (_) {
        /* optional */
      }
      setStorySortableEnabled(isEditOn())
      suppressItemClickUntil = performance.now() + 450
      session = null
    }

    const landOnPage = function ($page) {
      if (!$page?.length) return
      const active = require('./active')
      active.set($page, true)
      requestAnimationFrame(() => {
        instantCenterEl($page[0])
        requestAnimationFrame(() => instantCenterEl($page[0]))
      })
    }

    const onDragMove = function (e) {
      if (!session) return
      if (session.pointerId != null && e.pointerId != null && e.pointerId !== session.pointerId) return
      let x = e.clientX
      let y = e.clientY
      if (typeof x !== 'number' && e.touches?.[0]) {
        x = e.touches[0].clientX
        y = e.touches[0].clientY
      }
      if (typeof x !== 'number') return
      session.pointerX = x
      session.pointerY = y
      session.moved = true
      moveHelper(x, y)
      updateHoverUi()
      e.preventDefault?.()
    }

    const onDragCommit = function (e) {
      if (!session || session.committed) return
      if (
        session.pointerId != null &&
        e &&
        typeof e.pointerId === 'number' &&
        e.pointerId !== session.pointerId
      ) {
        return
      }
      if (session.suppressCommitUntil && performance.now() < session.suppressCommitUntil) {
        return
      }
      session.committed = true
      const $item = session.$item
      const $source = session.$sourcePage
      const $dest = session.$destPage
      const copy = session.action === 'copy'
      const $before = session.$before
      const didMove = !!session.moved
      teardown()
      const dropOk =
        didMove &&
        $item?.length &&
        $dest?.length &&
        itemDropAllowed($source, $dest, { copy })
      if (!dropOk) {
        select($item)
        landOnPage($source)
        return
      }
      placeItemOnPage($item, $dest, { copy, $before })
      try {
        require('./editor').hide({ clearSelection: true })
      } catch (_) {
        clear()
      }
      landOnPage($dest)
    }

    const onDragAbort = function (e) {
      if (!session) return
      if (session.pointerId != null && e && e.pointerId !== session.pointerId) return
      if (session.ignorePointerCancel && e?.type === 'lostpointercapture') {
        return
      }
      if (session.ignorePointerCancel && e?.type === 'pointercancel') {
        session.suppressCommitUntil = performance.now() + 400
        return
      }
      const $item = session.$item
      const $source = session.$sourcePage
      teardown()
      select($item)
      landOnPage($source)
    }

    const beginDrag = function ($item, clientX, clientY, pointerId) {
      cancelPending()
      try {
        require('./editor').noteDragStarted()
      } catch (_) {
        /* optional */
      }

      const itemEl = $item[0]
      const rect = itemEl.getBoundingClientRect()
      // Capture on body — capturing on the item loses the gesture when zoom/opacity changes.
      const captureEl = document.body
      session = {
        $item,
        $sourcePage: $item.closest('.page'),
        pointerX: clientX,
        pointerY: clientY,
        pointerId,
        action: 'move',
        $destPage: $(),
        $before: $(),
        leftSpacer: null,
        rightSpacer: null,
        edgeRaf: null,
        lastScrollTs: 0,
        // Only mark moved after the finger actually travels (see onDragMove).
        moved: false,
        ignorePointerCancel: true,
        suppressCommitUntil: performance.now() + 500,
        captureEl,
        itemHeight: itemEl.offsetHeight || Math.round(rect.height),
        itemWidth: itemEl.offsetWidth || Math.round(rect.width),
        grabOffsetX: clientX - rect.left,
        grabOffsetY: clientY - rect.top,
      }

      try {
        window.getSelection()?.removeAllRanges?.()
      } catch (_) {
        /* optional */
      }
      if (typeof pointerId === 'number') {
        try {
          captureEl.setPointerCapture?.(pointerId)
        } catch (_) {
          /* optional */
        }
      }

      $item.addClass(ORIGIN_CLASS)
      setStorySortableEnabled(false)
      try {
        dragHelpers.setPageSortableEnabled(false)
      } catch (_) {
        /* optional */
      }

      const helper = ensureHelper()
      helper.innerHTML = ''
      const clone = itemEl.cloneNode(true)
      clone.classList.remove(
        ORIGIN_CLASS,
        ORIGIN_HIDDEN,
        ORIGIN_COPY,
        'handle-selected',
        'toolbar-active',
        'wiki-block-chosen',
        'wiki-item-chosen',
      )
      clone
        .querySelectorAll(
          `.${CONTROLS_CLASS}, .${HANDLE_CLASS}, .${EDIT_CLASS}`,
        )
        .forEach(n => n.remove())
      while (clone.firstChild) helper.appendChild(clone.firstChild)
      // Children already live on helper — syncing the emptied clone is a no-op (R-028).
      syncDragItemTypography(itemEl, helper)
      helper.style.width = `${session.itemWidth}px`
      helper.style.minHeight = `${session.itemHeight}px`
      helper.classList.add('is-active')
      helper.setAttribute('aria-hidden', 'false')
      moveHelper(clientX, clientY)
      ensureOverlay()

      document.addEventListener('pointermove', onDragMove, { passive: false })
      document.addEventListener('touchmove', onDragMove, { passive: false })
      document.addEventListener('pointerup', onDragCommit)
      document.addEventListener('pointercancel', onDragAbort)
      document.addEventListener('touchend', onDragCommit)
      document.addEventListener('mouseup', onDragCommit)
      document.addEventListener('lostpointercapture', onDragAbort)
      window.addEventListener('pagehide', onDragAbort)

      requestAnimationFrame(() => {
        if (!session) return
        document.body.classList.add(DRAGGING)
        requestAnimationFrame(() => {
          if (!session) return
          document.body.classList.add(LINEUP_SHRUNK)
          ensureSpacers()
          const srcPage = $item.closest('.page')[0]
          if (srcPage) instantCenterEl(srcPage)
          moveHelper(session.pointerX, session.pointerY)
          // Hide origin only after the shrink UX is on screen.
          syncOriginVisibility(false)
          session.edgeRaf = requestAnimationFrame(edgeTick)
          updateHoverUi()
          // Allow a real finger-up shortly after shrink settles.
          session.suppressCommitUntil = performance.now() + 200
        })
      })
    }

    const pendingTick = function () {
      if (!pending) return
      pending.raf = null
      if (performance.now() - pending.t0 >= LONG_PRESS_MS) {
        const dx = pending.x - pending.startX
        const dy = pending.y - pending.startY
        if (dx * dx + dy * dy > BODY_SLOP_PX * BODY_SLOP_PX) {
          cancelPending()
          return
        }
        const { $item, x, y, pointerId } = pending
        cancelPending()
        beginDrag($item, x, y, pointerId)
        return
      }
      pending.raf = requestAnimationFrame(pendingTick)
    }

    const armPending = function ($item, e) {
      cancelPending()
      const main = mainEl()
      pending = {
        $item,
        x: e.clientX,
        y: e.clientY,
        startX: e.clientX,
        startY: e.clientY,
        pointerId: e.pointerId,
        t0: performance.now(),
        raf: null,
        // Snapshot scroll so tiny rubber-band / overflow noise does not cancel the hold.
        scrollLeft: main?.scrollLeft ?? 0,
        scrollTop: main?.scrollTop ?? 0,
        pageScrollTop: $item.closest('.page')?.[0]?.scrollTop ?? 0,
      }
      // Defeat .handle-selected { touch-action: pan-y } for the hold window.
      $item[0]?.classList?.add('wiki-longpress-pending')
      try {
        e.preventDefault()
      } catch (_) {
        /* optional */
      }
      pending.raf = requestAnimationFrame(pendingTick)
    }

    document.addEventListener(
      'scroll',
      e => {
        if (!pending) return
        const main = mainEl()
        const pageEl = pending.$item?.closest?.('.page')?.[0]
        const target = e.target
        let dx = 0
        let dy = 0
        if (main && (target === main || main.contains?.(target))) {
          dx = Math.abs((main.scrollLeft ?? 0) - (pending.scrollLeft ?? 0))
          dy = Math.abs((main.scrollTop ?? 0) - (pending.scrollTop ?? 0))
        }
        if (pageEl && (target === pageEl || pageEl.contains?.(target))) {
          dy = Math.max(dy, Math.abs((pageEl.scrollTop ?? 0) - (pending.pageScrollTop ?? 0)))
        }
        if (dx < 8 && dy < 8) return
        cancelPending()
        suppressItemClickUntil = performance.now() + 350
      },
      true,
    )
    document.addEventListener(
      'touchmove',
      e => {
        if (!pending || !e.touches?.[0]) return
        const t = e.touches[0]
        const dx = t.clientX - pending.startX
        const dy = t.clientY - pending.startY
        if (dx * dx + dy * dy > BODY_SLOP_PX * BODY_SLOP_PX) {
          cancelPending()
          suppressItemClickUntil = performance.now() + 350
          return
        }
        // Within slop: block native scroll so the hold can mature into lineup drag.
        e.preventDefault()
      },
      { capture: true, passive: false },
    )

    document.addEventListener(
      'pointerdown',
      function (e) {
        // Stale pending (e.g. cancel without pointerup) must not block new holds.
        if (pending && performance.now() - pending.t0 > 900) cancelPending()
        if (pending) return
        if (session) {
          // Prior gesture lost its pointer without touchend — clear stuck state.
          const stuckItem = session.$item
          const stuckSource = session.$sourcePage
          teardown()
          select(stuckItem)
          landOnPage(stuckSource)
          return
        }
        if (!isEditOn()) return
        // Match editor itemControlsApply: coarse pointer OR this event is touch.
        const touchLike =
          needsDragHandle() || e.pointerType === 'touch' || e.pointerType === 'pen'
        if (!touchLike) return
        if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return
        if (e.button != null && e.button !== 0) return
        const target = e.target
        if (!(target instanceof Element)) return
        // Handle is Sortable same-page reorder — do not start lineup drag from it.
        if (target.closest(`.${HANDLE_CLASS}`)) return
        if (target.closest(`.${EDIT_CLASS}, a, button, textarea, input`)) return

        const itemEl = target.closest('.page .story .item.handle-selected')
        if (!itemEl) return
        armPending($(itemEl), e)
      },
      true,
    )

    document.addEventListener(
      'pointermove',
      function (e) {
        if (!pending || e.pointerId !== pending.pointerId) return
        const dx = e.clientX - pending.startX
        const dy = e.clientY - pending.startY
        const dist2 = dx * dx + dy * dy
        pending.x = e.clientX
        pending.y = e.clientY
        if (dist2 > BODY_SLOP_PX * BODY_SLOP_PX) {
          cancelPending()
          suppressItemClickUntil = performance.now() + 350
          return
        }
        e.preventDefault?.()
      },
      { capture: true, passive: false },
    )

    const onPendingEnd = function (e) {
      if (pending && (!e || e.pointerId === pending.pointerId)) cancelPending()
    }
    document.addEventListener('pointerup', onPendingEnd, true)
    document.addEventListener('touchend', onPendingEnd, true)

    document.addEventListener(
      'contextmenu',
      function (e) {
        if (!needsDragHandle()) return
        if (e.target?.closest?.('.page .story .item.handle-selected, .item-drag-handle')) {
          e.preventDefault()
        }
      },
      true,
    )
  }

  return {
    bindLineupItemDrag,
    shouldSuppressItemClick,
  }
}

const bindStorySortableWithHandle = function (deps) {
  const {
    Sortable,
    HANDLE_CLASS,
    dragHelpers,
    getItem,
    getStoryItemOrder,
    commitSamePageReorder,
    isEditOn,
    syncDragItemTypography,
    nudgeStorySortableAt,
    vertScrollCandidates,
    storyPlaceholderHeightPx,
    PLACEHOLDER_SLOT_PX,
    clearItemDragCursorClasses,
  } = deps

  return function initHandleStorySortable($page) {
  const storyEl = $page.find('.story')[0]
  if (!storyEl) return null
  if (storyEl._wikiSortable) {
    storyEl._wikiSortable.destroy()
    storyEl._wikiSortable = null
  }
  document.body.classList.remove('wiki-item-handle-drag')
  if (storyEl._wikiPlaceholderLock) {
    storyEl.removeEventListener('pointerdown', storyEl._wikiPlaceholderLock, true)
    storyEl._wikiPlaceholderLock = null
  }
  if (storyEl._wikiPlaceholderUnlock) {
    storyEl.removeEventListener('pointerup', storyEl._wikiPlaceholderUnlock, true)
    storyEl.removeEventListener('pointercancel', storyEl._wikiPlaceholderUnlock, true)
    storyEl._wikiPlaceholderUnlock = null
  }

  const origCursor = $('body').css('cursor')
  let originalOrder = null
  let originalIndex = null
  let dragCancelled = false
  let dragItemSnapshot = null
  let dragPageElement = null
  let lastToStory = null
  let edgeScrollRaf = null
  let lastPointerY = null
  let lastPointerX = null
  let lastEdgeScrollTs = 0
  let placeholderBox = null

  const clearPlaceholderBox = function (el) {
    placeholderBox = null
    if (!el) return
    el.classList.remove('wiki-item-drag-sizing')
    el.style.height = ''
    el.style.minHeight = ''
    el.style.maxHeight = ''
    el.style.overflow = ''
    el.style.boxSizing = ''
    el.style.willChange = ''
  }

  const placeholderHeightPx = function (el) {
    const itemH = placeholderBox?.h || el?.offsetHeight || PLACEHOLDER_SLOT_PX
    return storyPlaceholderHeightPx(itemH, el)
  }

  const lockPlaceholderBox = function (el) {
    if (!el || !placeholderBox) return
    const h = placeholderHeightPx(el)
    el.classList.add('wiki-item-drag-sizing')
    el.style.boxSizing = 'border-box'
    el.style.height = `${h}px`
    el.style.minHeight = `${h}px`
    el.style.maxHeight = `${h}px`
    el.style.overflow = 'hidden'
  }

  const reserveSlotMinHeight = function (el) {
    if (!el || !placeholderBox) return
    el.style.boxSizing = 'border-box'
    el.style.minHeight = `${placeholderHeightPx(el)}px`
  }

  const lockStoryPlaceholder = function (story) {
    const ghost = story?.querySelector?.(':scope > .item-placeholder')
    if (ghost) lockPlaceholderBox(ghost)
  }

  const cancelDrag = function (e) {
    if (e.which === 27 && storyEl._wikiSortable) {
      dragCancelled = true
      document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true }))
      document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }))
    }
  }

  const eventXY = e => {
    if (!e) return { x: null, y: null }
    if (typeof e.clientX === 'number') return { x: e.clientX, y: e.clientY }
    const t = e.touches?.[0] || e.changedTouches?.[0]
    return { x: t?.clientX ?? null, y: t?.clientY ?? null }
  }

  // Shared desktop+mobile item-drag page scroll (Sortable's built-in scroll misses .page).
  const VERT_EDGE_PX = 120
  const VERT_SCROLL_PX_PER_SEC = 1100

  // Handle drag is same-page only: ignore finger X so a drift toward the page
  // edge does not retarget a neighbor story (which then refuses put and freezes).
  const handleDragNudgeX = () => {
    const story = dragPageElement?.[0]?.querySelector?.('.story')
    const r = story?.getBoundingClientRect()
    if (r && r.width > 0) return r.left + r.width / 2
    return typeof lastPointerX === 'number' ? lastPointerX : null
  }

  const stopEdgeScroll = function () {
    if (edgeScrollRaf != null) {
      cancelAnimationFrame(edgeScrollRaf)
      edgeScrollRaf = null
    }
    lastEdgeScrollTs = 0
  }

  const pullPlaceholderToStartIfAbove = function (y) {
    const story = dragPageElement?.[0]?.querySelector?.('.story')
    if (!story || typeof y !== 'number') return false
    const placeholder = [...story.children].find(el => el.classList.contains('item-placeholder'))
    if (!placeholder) return false
    const dragEl = Sortable.dragged
    const first = [...story.children].find(
      el =>
        el.classList.contains('item') &&
        !el.classList.contains('item-placeholder') &&
        !el.classList.contains('shadow-copy') &&
        el !== dragEl,
    )
    if (!first) return false
    const firstRect = first.getBoundingClientRect()
    const topZone = firstRect.top + Math.min(48, firstRect.height * 0.4)
    if (y >= topZone) return false
    if (placeholder.nextElementSibling !== first) story.insertBefore(placeholder, first)
    return true
  }

  const edgeScrollTick = function (ts) {
    edgeScrollRaf = null
    if (!Sortable.active || typeof lastPointerY !== 'number') return
    if (!lastEdgeScrollTs) lastEdgeScrollTs = ts
    const dt = Math.min(0.05, (ts - lastEdgeScrollTs) / 1000)
    lastEdgeScrollTs = ts

    const candidates = vertScrollCandidates(lastPointerX, dragPageElement?.[0], lastToStory)
    dragHelpers.scrollPageNearEdge(candidates, lastPointerY, dt, VERT_EDGE_PX, VERT_SCROLL_PX_PER_SEC)
    pullPlaceholderToStartIfAbove(lastPointerY)
    lockStoryPlaceholder(dragPageElement?.[0]?.querySelector?.('.story'))
    const nudgeX = handleDragNudgeX()
    if (typeof nudgeX === 'number') {
      nudgeStorySortableAt(nudgeX, lastPointerY, story => {
        lastToStory = story
        lockStoryPlaceholder(story)
      })
    }

    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const ensureEdgeScroll = function () {
    if (edgeScrollRaf != null) return
    lastEdgeScrollTs = 0
    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const onDragPointerMove = function (e) {
    const { x, y } = eventXY(e)
    if (typeof x === 'number') lastPointerX = x
    if (typeof y === 'number') {
      lastPointerY = y
      ensureEdgeScroll()
      pullPlaceholderToStartIfAbove(y)
      lockStoryPlaceholder(dragPageElement?.[0]?.querySelector?.('.story'))
      const nudgeX = handleDragNudgeX()
      if (typeof nudgeX === 'number') {
        nudgeStorySortableAt(nudgeX, y, story => {
          lastToStory = story
          lockStoryPlaceholder(story)
        })
      }
    }
  }

  const onHandlePointerDown = function (e) {
    const item = e.target?.closest?.('.item')
    if (!item || !storyEl.contains(item) || item.classList.contains('shadow-copy')) return
    const handle = e.target?.closest?.(`.${HANDLE_CLASS}`)
    if (!handle || !item.contains(handle)) return
    item.classList.add('wiki-item-drag-sizing')
    placeholderBox = { h: item.offsetHeight, w: item.offsetWidth }
  }
  const onHandlePointerUp = function (e) {
    if (Sortable.active) return
    storyEl.querySelectorAll('.wiki-item-drag-sizing').forEach(el => clearPlaceholderBox(el))
    const item = e.target?.closest?.('.item')
    if (item && storyEl.contains(item) && !item.classList.contains('item-placeholder')) {
      clearPlaceholderBox(item)
    } else {
      placeholderBox = null
    }
  }
  storyEl._wikiPlaceholderLock = onHandlePointerDown
  storyEl._wikiPlaceholderUnlock = onHandlePointerUp
  storyEl.addEventListener('pointerdown', onHandlePointerDown, true)
  storyEl.addEventListener('pointerup', onHandlePointerUp, true)
  storyEl.addEventListener('pointercancel', onHandlePointerUp, true)

  const sortable = Sortable.create(storyEl, {
    animation: 0,
    handle: `.${HANDLE_CLASS}`,
    delay: 0,
    distance: 0,
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 5,
    // Custom vert edge scroll (desktop+mobile) targets .page; Sortable's scroller misses it.
    scroll: false,
    bubbleScroll: false,
    forceAutoScrollFallback: false,
    // .shadow-copy keeps .item for looks but must not be sortable.
    draggable: '.item:not(.shadow-copy)',
    filter: 'a, button, input, textarea, .item-action-toolbar, .item-edit-button',
    preventOnFilter: false,
    invertSwap: true,
    // 0.5 keeps a dead center so short neighbors don't flip-flop after mid-list snaps.
    invertedSwapThreshold: 0.5,
    emptyInsertThreshold: 48,
    ghostClass: 'item-placeholder',
    chosenClass: 'wiki-item-chosen',
    dragClass: 'wiki-item-drag',
    group: {
      name: 'wiki-story',
      pull(to, from, dragEl, evt) {
        if (to !== from) return false
        return true
      },
      put(to, from, dragEl) {
        if (!(dragEl?.classList?.contains('item') && !dragEl.classList.contains('shadow-copy'))) return false
        if (to !== from) return false
        return true
      },
    },
    disabled: !isEditOn(),
    onChoose(evt) {
      const el = evt.item
      if (!el) return
      if (!placeholderBox) {
        placeholderBox = { h: el.offsetHeight, w: el.offsetWidth }
      }
      el.style.willChange = 'auto'
      reserveSlotMinHeight(el)
    },
    onUnchoose(evt) {
      clearPlaceholderBox(evt.item)
    },
    onStart(evt) {
      const syncHandleHelper = () => {
        const helper = document.querySelector('.sortable-fallback.item, .wiki-item-drag')
        if (!helper) return
        syncDragItemTypography(evt.item, helper)
        helper.style.height = 'auto'
        helper.style.maxHeight = 'none'
        helper.style.overflow = 'visible'
      }
      syncHandleHelper()
      requestAnimationFrame(syncHandleHelper)
      try {
        require('./editor').noteDragStarted()
      } catch (_) {
        /* optional */
      }
      try {
        dragHelpers.setPageSortableEnabled(false)
      } catch (_) {
        /* optional */
      }
      const $item = $(evt.item)
      originalOrder = getStoryItemOrder(evt.from)
      originalIndex = $('.item').index($item)
      dragCancelled = false
      document.body.classList.add('wiki-item-handle-drag')
      dragItemSnapshot = $.extend({}, getItem($item))
      dragPageElement = $item.data('pageElement')
      $('body').on('keydown', cancelDrag)
      {
        const { x, y } = eventXY(evt.originalEvent)
        if (typeof x === 'number') lastPointerX = x
        if (typeof y === 'number') lastPointerY = y
      }
      document.addEventListener('pointermove', onDragPointerMove)
      document.addEventListener('touchmove', onDragPointerMove, { passive: true })
      ensureEdgeScroll()
      lastToStory = null
      lockPlaceholderBox(evt.item)
    },
    onMove(evt, originalEvent) {
      if (evt.dragged?.classList?.contains('page')) return false
      if (evt.to?.classList?.contains('main')) return false
      const $item = $(evt.dragged)
      if (!$item.data('pageElement') && dragPageElement) {
        $item.data('pageElement', dragPageElement)
      }
      if (!$item.data('item') && dragItemSnapshot) {
        $item.data('item', $.extend({}, dragItemSnapshot))
      }
      lastToStory = evt.to
      const ghost =
        (evt.dragged?.classList?.contains('item-placeholder') && evt.dragged) ||
        evt.to?.querySelector?.('.item-placeholder') ||
        evt.from?.querySelector?.('.item-placeholder')
      if (ghost) lockPlaceholderBox(ghost)
      // Same-page handle reorder on a ghost is allowed; cross-page stays no-drop.
      if ($(evt.to).closest('.page').hasClass('ghost') && evt.from !== evt.to) return false
      // Handle drag: same story only (cross-page is long-press lineup drag).
      if (evt.from && evt.to && evt.from !== evt.to) {
        return false
      }
      const y =
        typeof originalEvent?.clientY === 'number'
          ? originalEvent.clientY
          : originalEvent?.touches?.[0]?.clientY
      if (typeof y === 'number' && pullPlaceholderToStartIfAbove(y)) return false
      return true
    },
    onEnd(evt) {
      document.body.classList.remove('wiki-item-handle-drag')
      clearItemDragCursorClasses()
      $('body').css('cursor', origCursor).off('keydown', cancelDrag)
      $('.wiki-item-drag, .sortable-fallback.item').css('cursor', '')
      document.removeEventListener('pointermove', onDragPointerMove)
      document.removeEventListener('touchmove', onDragPointerMove)
      stopEdgeScroll()
      lastPointerY = null
      lastPointerX = null
      const $item = $(evt.item)
      if (!$item.data('pageElement') && dragPageElement) {
        $item.data('pageElement', dragPageElement)
      }
      if (!$item.data('item') && dragItemSnapshot) {
        $item.data('item', $.extend({}, dragItemSnapshot))
      }
      const revert = () => {
        if (evt.from && evt.oldIndex != null) {
          const ref = evt.from.children[evt.oldIndex]
          if (ref) evt.from.insertBefore(evt.item, ref)
          else evt.from.appendChild(evt.item)
        }
      }
      // If an item somehow landed under .main (page lineup), put it back in its story.
      if ($item.parent().hasClass('main')) {
        revert()
      }
      const reordered = evt.oldIndex !== evt.newIndex
      if (dragCancelled) {
        revert()
      } else if (reordered && evt.from === evt.to) {
        commitSamePageReorder($item, originalIndex, originalOrder)
      } else if (evt.from !== evt.to) {
        revert()
      }
      clearPlaceholderBox(evt.item)
      dragItemSnapshot = null
      dragPageElement = null
      lastToStory = null
      try {
        dragHelpers.setPageSortableEnabled(true)
      } catch (_) {
        /* optional */
      }
    },
  })

  storyEl._wikiSortable = sortable
  return sortable
  }
}

/**
 * Wire mobile binders into sortable.js.
 * @param {object} deps — shared helpers from lib/sortable.js
 */
const bindMobileSortable = function (deps) {
  const {
    Sortable,
    setMobileHandlers,
    isMobileViewport,
    isEditOn,
    dragHelpers,
    getItem,
    getStoryItemOrder,
    commitSamePageReorder,
    commitCrossPageAdd,
    aliasItem,
    equals,
    pageHandler,
    plugin,
    lineup,
    state,
    active,
    pagesInMain,
    centerInMain,
    scrollLineupNearEdge,
    clamp,
    withDragArtifactsHidden,
    setStorySortableEnabled,
    setPageSortableEnabled,
    vertScrollCandidates,
    syncDragItemTypography,
    nudgeStorySortableAt,
    storyPlaceholderHeightPx,
    PLACEHOLDER_SLOT_PX,
    clearItemDragCursorClasses,
    pageDropBounds,
    DRAG_CLASS,
    journalUnderPoint,
    clearMergeHover,
    handleMerging,
    clearTextSelection,
    JOURNAL_BODY_SLOP_PX,
    MERGING,
    SELECTED_CLASS,
    VERT_EDGE_PX,
    VERT_SCROLL_PX_PER_SEC,
    suppressClickUntil,
    setSuppressClickUntil,
  } = deps

  const LINEUP_SHRUNK = 'wiki-page-lineup-shrunk'

  const ensureLineupSpacers = (holder, main) => {
    if (!main || !holder) return
    const pad = Math.max(140, Math.round(main.clientWidth * 0.58))
    if (!holder.leftSpacer) {
      holder.leftSpacer = document.createElement('div')
      holder.leftSpacer.className = 'wiki-page-drag-spacer wiki-page-drag-spacer-left'
      holder.leftSpacer.setAttribute('aria-hidden', 'true')
      main.insertBefore(holder.leftSpacer, main.firstChild)
    }
    if (!holder.rightSpacer) {
      holder.rightSpacer = document.createElement('div')
      holder.rightSpacer.className = 'wiki-page-drag-spacer wiki-page-drag-spacer-right'
      holder.rightSpacer.setAttribute('aria-hidden', 'true')
      main.appendChild(holder.rightSpacer)
    }
    for (const sp of [holder.leftSpacer, holder.rightSpacer]) {
      sp.style.flex = `0 0 ${pad}px`
      sp.style.width = `${pad}px`
      sp.style.minWidth = `${pad}px`
    }
  }

  const removeLineupSpacers = holder => {
    holder?.leftSpacer?.remove()
    holder?.rightSpacer?.remove()
    if (holder) {
      holder.leftSpacer = null
      holder.rightSpacer = null
    }
  }

  dragHelpers.ensureLineupSpacers = ensureLineupSpacers
  dragHelpers.removeLineupSpacers = removeLineupSpacers
  dragHelpers.LINEUP_SHRUNK = LINEUP_SHRUNK

  const resolveLineupInsertY = function (pageEl, y) {
    const band = pageDropBounds(pageEl)
    if (!band || typeof y !== 'number') return { y, forceEnd: false }
    const anchorY = clamp(y, band.bandTop, band.bandBottom)
    const belowStory = anchorY >= band.storyRect.bottom - 1
    const probeY = belowStory ? band.storyRect.bottom - 1 : anchorY
    return { y: probeY, forceEnd: belowStory }
  }

  const itemDropAllowed = function ($sourcePage, $destPage, { copy = false } = {}) {
    if (!$sourcePage?.length || !$destPage?.length) return false
    const sourceIsReadOnly = $sourcePage.hasClass('ghost') || $sourcePage.hasClass('remote')
    const samePage = equals($sourcePage, $destPage)
    if ($destPage.hasClass('ghost') && !samePage) return false
    const duplicatePage = !samePage && $sourcePage.attr('id') === $destPage.attr('id')
    const copying = copy || sourceIsReadOnly
    if (duplicatePage && !copying) return false
    return true
  }

  const placeItemOnPage = function ($item, $destPage, opts = {}) {
    if (!$item?.length || !$destPage?.length) return
    const wantCopy = !!opts.copy
    let item = getItem($item)
    if (!item) return

    const $sourcePage = $item.data('pageElement') || $item.parents('.page:first')
    if (!$sourcePage?.length) return

    const sourceIsReadOnly = $sourcePage.hasClass('ghost') || $sourcePage.hasClass('remote')
    const copying = wantCopy || sourceIsReadOnly
    const samePage = equals($sourcePage, $destPage)
    if ($destPage.hasClass('ghost') && !samePage) return
    const duplicatePage = !samePage && $sourcePage.attr('id') === $destPage.attr('id')
    if (duplicatePage && !copying) return

    const $story = $destPage.find('.story').first()
    if (!$story.length) return

    let dragAttribution = null
    if (!$sourcePage.hasClass('ghost')) {
      dragAttribution = { page: $sourcePage.data().data['title'] }
      if ($sourcePage.data().site != null) dragAttribution.site = $sourcePage.data().site
    }

    const removedTo = { page: $destPage.data().data['title'] }
    const $before = opts.$before?.length ? opts.$before : $()
    const originalIndex = $('.item').index($item)

    const insertNode = $node => {
      if ($before.length) $before.after($node)
      else $story.prepend($node)
    }

    if (samePage && !copying) {
      const originalOrder = getStoryItemOrder($story)
      insertNode($item)
      commitSamePageReorder($item, originalIndex, originalOrder)
      return
    }

    let $placed = $item
    if (copying) {
      $placed = $item
        .clone(false)
        .removeClass(
          'item-placeholder wiki-item-chosen wiki-item-drag handle-selected toolbar-active is-copy-preview shadow-copy wiki-item-lineup-origin',
        )
        .css({ width: '', height: '', position: '', zIndex: '', display: '', opacity: '' })
      $placed.data('item', $.extend({}, item))
    } else {
      pageHandler.put($sourcePage, { id: item.id, type: 'remove', removedTo })
    }

    insertNode($placed)
    commitCrossPageAdd({
      $placed,
      $destPage,
      $before,
      copying,
      originalIndex,
      dragAttribution,
    })
  }

  const MOVE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">' +
    '<path fill="currentColor" d="M4 11h9.2l-2.6-2.6L12 7l5 5-5 5-1.4-1.4L13.2 13H4v-2zm16 8h-2V5h2v14z"/>' +
    '</svg>'

  const COPY_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">' +
    '<path fill="currentColor" d="M8 7h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zm-3 3H4V4h12v1H5v5z"/>' +
    '</svg>'

  const {
    HANDLE_CLASS,
    EDIT_CLASS,
    TOOLBAR_CLASS,
    needsDragHandle,
    ensure,
    clear,
    select,
    getSelectedId,
    clearContent,
  } = itemToolbar({ isEditOn })

  const clearJournalSelection = function () {
    document.querySelectorAll(`.journal.${SELECTED_CLASS}`).forEach(el => {
      el.classList.remove(SELECTED_CLASS)
    })
  }

  const selectJournal = function ($journal) {
    if (!$journal?.length) return null
    if (!isEditOn() || !needsDragHandle()) {
      clearJournalSelection()
      return null
    }
    clear()
    clearJournalSelection()
    $journal.addClass(SELECTED_CLASS)
    return $journal
  }

  const LONG_PRESS_MS = 380

  const { bindLineupItemDrag, shouldSuppressItemClick } = makeLineupItemDrag({
    dragHelpers,
    MOVE_SVG,
    COPY_SVG,
    needsDragHandle,
    isEditOn,
    clear,
    select,
    HANDLE_CLASS,
    EDIT_CLASS,
    TOOLBAR_CLASS,
    CONTROLS_CLASS: 'item-action-toolbar',
    setStorySortableEnabled,
    vertScrollCandidates,
    LINEUP_SHRUNK,
    syncDragItemTypography,
    resolveLineupInsertY,
    cappedPlaceholderHeightPx: storyPlaceholderHeightPx,
    placeItemOnPage,
    itemDropAllowed,
  })

  const bindJournalMergeMobile = bindJournalMergeTouch({
    dragHelpers,
    vertScrollCandidates,
    VERT_EDGE_PX,
    VERT_SCROLL_PX_PER_SEC,
    LONG_PRESS_MS,
    JOURNAL_BODY_SLOP_PX,
    MERGING,
    LINEUP_SHRUNK,
    SELECTED_CLASS,
    journalUnderPoint,
    clearMergeHover,
    handleMerging,
    clearTextSelection,
    isEditOn,
    needsDragHandle,
    setStorySortableEnabled,
    selectJournal,
    clearJournalSelection,
    suppressClickUntil,
    setSuppressClickUntil,
  })

  let initMobilePageSortable = null
  const initHandleStorySortable = bindStorySortableWithHandle({
    Sortable,
    HANDLE_CLASS,
    dragHelpers,
    getItem,
    getStoryItemOrder,
    commitSamePageReorder,
    isEditOn,
    syncDragItemTypography,
    nudgeStorySortableAt,
    vertScrollCandidates,
    storyPlaceholderHeightPx,
    PLACEHOLDER_SLOT_PX,
    clearItemDragCursorClasses,
  })

  setMobileHandlers({
    bindMainSortable(mainEl) {
      if (isMobileViewport()) {
        if (!initMobilePageSortable) {
          initMobilePageSortable = bindMobilePageDrag({
            Sortable,
            DRAG_CLASS,
            lineup,
            state,
            plugin,
            active,
            pagesInMain,
            centerInMain,
            scrollLineupNearEdge,
            ensureLineupSpacers,
            removeLineupSpacers,
            clamp,
            withDragArtifactsHidden,
            setStorySortableEnabled,
            isEditOn,
          })
        }
        return initMobilePageSortable(mainEl)
      }
      return deps.bindMainSortable(mainEl)
    },
    bindStorySortable($page) {
      if (needsDragHandle()) return initHandleStorySortable($page)
      return deps.bindStorySortable($page)
    },
    bindJournalMerge($page) {
      deps.bindJournalMerge($page)
    },
  })

  return {
    HANDLE_CLASS,
    EDIT_CLASS,
    needsDragHandle,
    ensure,
    clear,
    select,
    getSelectedId,
    clearContent,
    bindLineupItemDrag,
    shouldSuppressItemClick,
    bindJournalMerge: bindJournalMergeMobile,
    selectJournal,
    clearJournalSelection,
    ensureLineupSpacers,
    removeLineupSpacers,
    LINEUP_SHRUNK,
  }
}

module.exports = {
  bindMobilePageDrag,
  itemToolbar,
  bindJournalMergeTouch,
  makeLineupItemDrag,
  bindStorySortableWithHandle,
  bindMobileSortable,
}
